/**
 * useHomeDesk — the data half of the /f8chat desk (PPL-416).
 *
 * Owns: the feed (homeFeedEurW on mount and on window focus, throttled),
 * the per-uid localStorage cache, the cold opener (homeOpenerEurW, only
 * when the feed arrives with `opener: null`, always with the feed's stage
 * and modules), local dismiss / undo through homeDismissEurW, and the
 * overflow expansion. It knows nothing about the router or the composer;
 * HomeDesk.tsx turns its state into zones.
 *
 * Status (§9):
 *   loading — skeleton opener + two rows, no spinner. Held for at most
 *             FIRST_PAINT_FALLBACK_MS, then the cache (stale) or the bare
 *             line (error) while the fetch keeps going.
 *   ready   — the server's feed.
 *   stale   — the cached feed, dimmed, stamped "as of" (offline / error).
 *   error   — nothing to show but the one static line + composer.
 *
 * Ranking is the server's: `feed.modules` renders in order; a local dismiss
 * only removes, an undo only puts back at the same index. Nothing here
 * re-ranks or backfills.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { homeDismissEurW, homeFeedEurW, homeOpenerEurW } from '../../firebaseConfig';
import type { HomeFeed, HomeModule, HomeOpener } from '../../types/home';
import { trackHomeViewed, trackModuleDismissed, trackModulesShown } from './homeAnalytics';
import {
  clearHomeFeedStale,
  isFresh,
  isHomeFeedStale,
  normalizeFeed,
  readHomeFeedCache,
  writeHomeFeedCache,
} from './homeFeedCache';
import { isKnownModuleType } from './moduleRegistry';
import { undoLabel } from './format';

export type HomeDeskStatus = 'loading' | 'ready' | 'stale' | 'error';

/** How long zone B stays on the skeleton before falling back (spec §2). */
export const FIRST_PAINT_FALLBACK_MS = 800;
/** Window focus re-fetches at most this often. */
export const FOCUS_REFRESH_MIN_MS = 60_000;
/** The undo row lingers this long after a dismiss (PPL-417: "~6 s"). */
export const UNDO_LINGER_MS = 6_000;

/**
 * PPL-421 — one network fetch per uid at a time ACROSS hook instances. A
 * remount inside the first fetch's window (seen on first paint: two
 * homeFeedEurW calls 27 ms apart, one desk view) adopts the in-flight
 * result instead of calling the server again.
 */
const feedInFlight = new Map<string, Promise<HomeFeed>>();
/** homeOpenerEurW's cap on the desk it is handed. */
const OPENER_MAX_MODULES = 12;

export interface HomeUndo {
  /** The undo row's line — `undoLabel(until)`. */
  text: string;
  dismissKey: string;
}

export interface HomeDeskController {
  status: HomeDeskStatus;
  feed: HomeFeed | null;
  /** `feed.modules`, plus `feed.overflow` once expanded. In server order. */
  modules: HomeModule[];
  /** The cold opener is in flight — zone B shows the skeleton meanwhile. */
  openerPending: boolean;
  offline: boolean;
  expanded: boolean;
  undo: HomeUndo | null;
  refresh: () => void;
  /** Optimistic: the module leaves now; homeDismissEurW decides the window. */
  /** `silent` (M8 "someone else"): dismiss the served match with no undo row, then re-fetch for the next face. */
  dismiss: (module: HomeModule, opts?: { silent?: boolean }) => void;
  undoDismiss: () => void;
  expandOverflow: () => void;
}

export function getTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function feedKey(feed: HomeFeed): string {
  return `${feed.stage}:${feed.asOf}`;
}

function firebaseCode(err: unknown): string | null {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : null;
}

interface Removed {
  module: HomeModule;
  index: number;
  fromOverflow: boolean;
}

export function useHomeDesk(uid: string | null | undefined): HomeDeskController {
  const tz = useMemo(getTimeZone, []);
  const [feed, setFeed] = useState<HomeFeed | null>(null);
  const [status, setStatus] = useState<HomeDeskStatus>('loading');
  const [openerPending, setOpenerPending] = useState(false);
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  const [expanded, setExpanded] = useState(false);
  const [undo, setUndo] = useState<HomeUndo | null>(null);

  const feedRef = useRef<HomeFeed | null>(null);
  const statusRef = useRef<HomeDeskStatus>('loading');
  const inFlight = useRef(false);
  const lastFetchAt = useRef(0);
  const alive = useRef(true);
  const openerAskedFor = useRef<string | null>(null);
  // §11: `home_viewed` once per open, `home_module_shown` once per module per open.
  const viewed = useRef(false);
  const shownKeys = useRef<Set<string>>(new Set());
  const removed = useRef<Removed | null>(null);
  // Restore-on-error is tracked per key: `removed` is the single undo
  // affordance and a second dismiss supersedes it, but a failed call for an
  // earlier dismiss must still put that module back.
  const restoreOnError = useRef<Map<string, Removed>>(new Map());
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setFeedBoth = useCallback((f: HomeFeed | null) => {
    feedRef.current = f;
    setFeed(f);
  }, []);
  const setStatusBoth = useCallback((s: HomeDeskStatus) => {
    statusRef.current = s;
    setStatus(s);
  }, []);

  const clearUndo = useCallback(() => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = null;
    removed.current = null;
    setUndo(null);
  }, []);

  const trackShown = useCallback((stage: HomeFeed['stage'], list: HomeModule[], offset = 0) => {
    const fresh = list
      .map((module, i) => ({ module, position: offset + i }))
      .filter(({ module }) => !shownKeys.current.has(module.dismissKey));
    fresh.forEach(({ module }) => shownKeys.current.add(module.dismissKey));
    if (fresh.length) trackModulesShown(stage, fresh);
  }, []);

  /** A new payload replaces everything local: expansion and the pending undo. */
  const adopt = useCallback(
    (next: HomeFeed, source: 'network' | 'cache', latencyMs: number) => {
      const key = feedKey(next);
      const changed = !feedRef.current || feedKey(feedRef.current) !== key;
      setFeedBoth(next);
      if (changed) {
        setExpanded(false);
        clearUndo();
      }
      if (!viewed.current) {
        viewed.current = true;
        trackHomeViewed(next, { latencyMs, fromCache: source === 'cache' });
      }
      trackShown(next.stage, next.modules);
    },
    [clearUndo, setFeedBoth, trackShown],
  );

  const fetchFeed = useCallback(
    async (opts: { silent: boolean; stale?: boolean }) => {
      if (!uid || inFlight.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setOffline(true);
        if (feedRef.current) setStatusBoth('stale');
        return;
      }
      inFlight.current = true;
      const started = Date.now();
      try {
        // PPL-488: the stale flag is spent HERE, where a request is actually
        // issued — not at the call site, which cannot see the offline bail or
        // the in-flight early return above. A request that started before the
        // profile save would answer with the old stage, so it is dropped
        // rather than adopted.
        if (opts.stale) {
          feedInFlight.delete(uid);
          clearHomeFeedStale(uid);
        }
        let pending = feedInFlight.get(uid);
        if (!pending) {
          pending = homeFeedEurW({ surface: 'web', tz }).then((res) => {
            const parsed = normalizeFeed((res as { data?: unknown })?.data);
            if (!parsed) throw new Error('homeFeedEurW: not a feed');
            return parsed;
          });
          feedInFlight.set(uid, pending);
          const settled = pending;
          settled.finally(() => {
            if (feedInFlight.get(uid) === settled) feedInFlight.delete(uid);
          }).catch(() => undefined);
        }
        const next = await pending;
        if (!alive.current) return;
        lastFetchAt.current = Date.now();
        adopt(next, 'network', Date.now() - started);
        setStatusBoth('ready');
        setOffline(false);
        writeHomeFeedCache(uid, next);
      } catch (err) {
        if (!alive.current) return;
        console.warn('useHomeDesk: feed fetch failed', err);
        if (feedRef.current) {
          setStatusBoth('stale');
        } else if (!opts.silent || statusRef.current === 'loading') {
          const cached = readHomeFeedCache(uid);
          if (cached) {
            adopt(cached.feed, 'cache', 0);
            setStatusBoth('stale');
          } else {
            setStatusBoth('error');
          }
        }
      } finally {
        inFlight.current = false;
      }
    },
    [adopt, setStatusBoth, tz, uid],
  );

  // ── mount / uid change: cache first, then the network ──────────────
  useEffect(() => {
    alive.current = true;
    feedRef.current = null;
    setFeed(null);
    setExpanded(false);
    clearUndo();
    openerAskedFor.current = null;
    viewed.current = false;
    shownKeys.current = new Set();
    setOpenerPending(false);
    if (!uid) {
      setStatusBoth('error');
      return () => {
        alive.current = false;
      };
    }
    setStatusBoth('loading');
    // PPL-488: something the desk cannot see moved the user's stage (a
    // profile save). Do not paint the cached feed as if it were current —
    // wait for the server, the way a cold mount does.
    const stale = isHomeFeedStale(uid);
    const cached = stale ? null : readHomeFeedCache(uid);
    let fallback: ReturnType<typeof setTimeout> | null = null;
    if (cached && isFresh(cached)) {
      adopt(cached.feed, 'cache', 0);
      setStatusBoth('ready');
      void fetchFeed({ silent: true, stale });
    } else {
      fallback = setTimeout(() => {
        if (!alive.current || statusRef.current !== 'loading') return;
        if (cached) {
          adopt(cached.feed, 'cache', 0);
          setStatusBoth('stale');
        } else {
          setStatusBoth('error');
        }
      }, FIRST_PAINT_FALLBACK_MS);
      void fetchFeed({ silent: false, stale });
    }
    return () => {
      alive.current = false;
      if (fallback) clearTimeout(fallback);
    };
    // adopt / fetchFeed / clearUndo are stable for a given uid + tz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // ── window focus (throttled) + online / offline ────────────────────
  useEffect(() => {
    if (!uid) return;
    const maybeRefresh = () => {
      if (document.visibilityState === 'hidden') return;
      // A stale desk (PPL-488) refetches whatever the throttle says.
      const stale = isHomeFeedStale(uid);
      if (!stale && Date.now() - lastFetchAt.current < FOCUS_REFRESH_MIN_MS) return;
      void fetchFeed({ silent: true, stale });
    };
    const onOnline = () => {
      setOffline(false);
      void fetchFeed({ silent: true });
    };
    const onOffline = () => {
      setOffline(true);
      if (feedRef.current) setStatusBoth('stale');
    };
    window.addEventListener('focus', maybeRefresh);
    document.addEventListener('visibilitychange', maybeRefresh);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      document.removeEventListener('visibilitychange', maybeRefresh);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [uid, fetchFeed, setStatusBoth]);

  // ── the cold opener: only when the feed arrived without one ────────
  useEffect(() => {
    if (!uid || !feed || feed.opener || offline) return;
    const key = feedKey(feed);
    if (openerAskedFor.current === key) return;
    openerAskedFor.current = key;
    setOpenerPending(true);
    // The server validates the desk it is handed for shape: known types
    // only, at most OPENER_MAX_MODULES. An unknown type would fail the
    // whole call, so it is left out here (the registry renders it as
    // nothing anyway).
    const modules = feed.modules.filter((m) => isKnownModuleType(m.type)).slice(0, OPENER_MAX_MODULES);
    homeOpenerEurW({ surface: 'web', tz, stage: feed.stage, modules })
      .then((res) => {
        if (!alive.current) return;
        const data = (res as { data?: { opener?: unknown; prompts?: unknown } })?.data;
        const opener = data?.opener as HomeOpener | null | undefined;
        if (!opener || typeof opener !== 'object' || typeof opener.text !== 'string' || !opener.text) return;
        const prompts = Array.isArray(data?.prompts) ? (data!.prompts as unknown[]).filter((p): p is string => typeof p === 'string' && p.length > 0) : [];
        const current = feedRef.current;
        if (!current || feedKey(current) !== key) return;
        const merged: HomeFeed = {
          ...current,
          opener: { text: opener.text, emphasis: typeof opener.emphasis === 'string' ? opener.emphasis : null, prompt: typeof opener.prompt === 'string' ? opener.prompt : '' },
          prompts: prompts.length ? prompts : current.prompts,
        };
        setFeedBoth(merged);
        writeHomeFeedCache(uid, merged);
      })
      .catch((err) => {
        console.warn('useHomeDesk: opener failed', err);
      })
      .finally(() => {
        if (alive.current) setOpenerPending(false);
      });
  }, [uid, feed, offline, tz, setFeedBoth]);

  // ── dismiss / undo ─────────────────────────────────────────────────
  const dismiss = useCallback(
    (module: HomeModule, opts?: { silent?: boolean }) => {
      const current = feedRef.current;
      if (!uid || !current || !module.dismissable) return;
      // One pending undo at a time: a second dismiss forgets the first.
      clearUndo();
      let index = current.modules.findIndex((m) => m.dismissKey === module.dismissKey);
      let fromOverflow = false;
      if (index < 0) {
        index = current.overflow.findIndex((m) => m.dismissKey === module.dismissKey);
        fromOverflow = index >= 0;
      }
      if (index < 0) return;
      const without = (list: HomeModule[]) => list.filter((m) => m.dismissKey !== module.dismissKey);
      const next: HomeFeed = fromOverflow
        ? { ...current, overflow: without(current.overflow), more: Math.max(0, current.more - 1) }
        : { ...current, modules: without(current.modules) };
      removed.current = { module, index, fromOverflow };
      restoreOnError.current.set(module.dismissKey, { module, index, fromOverflow });
      setFeedBoth(next);
      writeHomeFeedCache(uid, next);

      homeDismissEurW({ dismissKey: module.dismissKey, tz })
        .then((res) => {
          restoreOnError.current.delete(module.dismissKey);
          if (!alive.current) return;
          const until = (res as { data?: { until?: unknown } })?.data?.until;
          const untilIso = typeof until === 'string' ? until : null;
          trackModuleDismissed(current.stage, module, untilIso);
          if (removed.current?.module.dismissKey !== module.dismissKey) return;
          if (opts?.silent) {
            // M8 "someone else": the server serves the same match all day
            // until its key is dismissed, so a bare re-fetch changed nothing.
            // Dismiss, then re-fetch for the next face; no undo row — the app
            // does the same.
            removed.current = null;
            void fetchFeed({ silent: true });
            return;
          }
          setUndo({ text: undoLabel(untilIso), dismissKey: module.dismissKey });
          undoTimer.current = setTimeout(() => {
            if (removed.current?.module.dismissKey === module.dismissKey) clearUndo();
          }, UNDO_LINGER_MS);
        })
        .catch((err) => {
          if (!alive.current) return;
          // failed-precondition = the server says this one cannot be
          // dismissed; anything else = it did not happen. Both: put it back.
          console.warn(`useHomeDesk: dismiss ${module.dismissKey} failed (${firebaseCode(err) ?? 'unknown'})`, err);
          const r = restoreOnError.current.get(module.dismissKey);
          restoreOnError.current.delete(module.dismissKey);
          if (!r) return;
          if (removed.current?.module.dismissKey === module.dismissKey) clearUndo();
          const live = feedRef.current;
          if (!live) return;
          const restore = (list: HomeModule[]) => {
            const copy = list.filter((m) => m.dismissKey !== r.module.dismissKey);
            copy.splice(Math.min(r.index, copy.length), 0, r.module);
            return copy;
          };
          const back: HomeFeed = r.fromOverflow
            ? { ...live, overflow: restore(live.overflow), more: live.more + 1 }
            : { ...live, modules: restore(live.modules) };
          setFeedBoth(back);
          writeHomeFeedCache(uid, back);
        });
    },
    [clearUndo, fetchFeed, setFeedBoth, tz, uid],
  );

  const undoDismiss = useCallback(() => {
    const r = removed.current;
    const live = feedRef.current;
    if (!uid || !r || !live) return;
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = null;
    removed.current = null;
    setUndo(null);
    const restore = (list: HomeModule[]) => {
      const copy = list.filter((m) => m.dismissKey !== r.module.dismissKey);
      copy.splice(Math.min(r.index, copy.length), 0, r.module);
      return copy;
    };
    const back: HomeFeed = r.fromOverflow
      ? { ...live, overflow: restore(live.overflow), more: live.more + 1 }
      : { ...live, modules: restore(live.modules) };
    setFeedBoth(back);
    writeHomeFeedCache(uid, back);
    homeDismissEurW({ dismissKey: r.module.dismissKey, undo: true, tz }).catch((err) => {
      console.warn(`useHomeDesk: undo ${r.module.dismissKey} failed`, err);
    });
  }, [setFeedBoth, tz, uid]);

  const expandOverflow = useCallback(() => {
    setExpanded(true);
    const f = feedRef.current;
    if (f) trackShown(f.stage, f.overflow, f.modules.length);
  }, [trackShown]);
  const refresh = useCallback(() => {
    void fetchFeed({ silent: true });
  }, [fetchFeed]);

  const modules = useMemo(() => {
    if (!feed) return [];
    return expanded ? [...feed.modules, ...feed.overflow] : feed.modules;
  }, [feed, expanded]);

  return { status, feed, modules, openerPending, offline, expanded, undo, refresh, dismiss, undoDismiss, expandOverflow };
}
